import { createFeature, createReducer, on } from '@ngrx/store';
import {
  submitCredentials,
  submitCredentialsSuccess,
  submitCredentialsFailure,
  verifyOtp,
  verifyOtpSuccess,
  verifyOtpFailure,
  verifyGoogle,
  verifyGoogleSuccess,
  verifyGoogleFailure,
  startGoogleSetup,
  startGoogleSetupSuccess,
  startGoogleSetupFailure,
  googleAuthStepEntered,
  resendOtp,
  resendOtpSuccess,
  resendOtpFailure,
  reset,
  setLoading,
  setError,
  clearError,
  backToCredentials,
} from './login.actions';
import { GoogleAuthLoginSetup } from '../../../models/auth.model';
import {
  LOGIN_STEP_GATE,
  isGoogleAuthStep,
  parseLoginStep,
} from './login-step';

export interface LoginState {
  username: string | null;
  sessionToken: string | null;
  loginMethod: string | null;
  password: string | null;
  googleAuthRequired: boolean;
  /** The member has no authenticator yet and enrols on the G-Auth screen. */
  googleAuthSetupRequired: boolean;
  /** The pending enrolment's QR code, once fetched. */
  googleAuthSetup: GoogleAuthLoginSetup | null;
  canGoToStep: number;
  loading: boolean;
  error: string | null;
}

export const initialState: LoginState = {
  username: null,
  sessionToken: null,
  loginMethod: null,
  password: null,
  googleAuthRequired: false,
  googleAuthSetupRequired: false,
  googleAuthSetup: null,
  canGoToStep: 1,
  loading: false,
  error: null,
};

export const loginFeature = createFeature({
  name: 'login',
  reducer: createReducer(
    initialState,
    // Submitting credentials starts a new sign-in: an abandoned one's session
    // token and step progress may not carry into it (F-037).
    on(submitCredentials, () => ({
      ...initialState,
      loading: true,
    })),
    on(submitCredentialsSuccess, (state, payload) => {
      // The effect refuses a step it cannot read (F-033), so `null` here only
      // means a hand-built action: it opens nothing.
      const nextStep = parseLoginStep(payload.nextStep);
      const googleRequired = isGoogleAuthStep(nextStep);
      return {
        ...state,
        username: payload.username,
        sessionToken: payload.sessionToken ?? null,
        loginMethod: payload.loginMethod ?? null,
        password: payload.password,
        googleAuthRequired: googleRequired,
        googleAuthSetupRequired: nextStep === 'GOOGLE_AUTH_SETUP',
        canGoToStep: nextStep ? LOGIN_STEP_GATE[nextStep] : state.canGoToStep,
        loading: false,
        error: null,
      };
    }),
    on(submitCredentialsFailure, (state, { error }) => ({
      ...state,
      loading: false,
      error,
    })),
    on(verifyOtp, state => ({
      ...state,
      loading: true,
      error: null,
    })),
    on(verifyOtpSuccess, (state, payload) => {
      if (payload.isComplete) {
        return initialState;
      }
      const nextStep = parseLoginStep(payload.nextStep);
      const googleRequired = isGoogleAuthStep(nextStep);
      return {
        ...state,
        sessionToken: payload.sessionToken ?? state.sessionToken,
        loginMethod: payload.loginMethod ?? state.loginMethod,
        googleAuthRequired: googleRequired,
        googleAuthSetupRequired: nextStep === 'GOOGLE_AUTH_SETUP',
        canGoToStep: nextStep
          ? Math.max(state.canGoToStep, LOGIN_STEP_GATE[nextStep])
          : state.canGoToStep,
        loading: false,
        error: null,
      };
    }),
    on(verifyOtpFailure, (state, { error }) => ({
      ...state,
      loading: false,
      error,
    })),
    on(verifyGoogle, state => ({
      ...state,
      loading: true,
      error: null,
    })),
    on(verifyGoogleSuccess, () => initialState),
    on(startGoogleSetup, state => ({
      ...state,
      loading: true,
      error: null,
    })),
    on(startGoogleSetupSuccess, (state, { setup }) => ({
      ...state,
      googleAuthSetup: setup,
      loading: false,
    })),
    on(startGoogleSetupFailure, (state, { error }) => ({
      ...state,
      loading: false,
      error,
    })),
    on(googleAuthStepEntered, (_state, { sessionToken, setupRequired }) => ({
      ...initialState,
      sessionToken,
      googleAuthRequired: true,
      googleAuthSetupRequired: setupRequired,
      canGoToStep: LOGIN_STEP_GATE.GOOGLE_AUTH,
    })),
    on(verifyGoogleFailure, (state, { error }) => ({
      ...state,
      loading: false,
      error,
    })),
    on(resendOtp, state => ({
      ...state,
      loading: true,
      error: null,
    })),
    on(resendOtpSuccess, (state, payload) => {
      const nextStep = parseLoginStep(payload.nextStep);
      const googleRequired = isGoogleAuthStep(nextStep);
      return {
        ...state,
        sessionToken: payload.sessionToken ?? state.sessionToken,
        loginMethod: payload.loginMethod ?? state.loginMethod,
        googleAuthRequired: googleRequired,
        googleAuthSetupRequired: nextStep === 'GOOGLE_AUTH_SETUP',
        canGoToStep: nextStep
          ? Math.max(state.canGoToStep, LOGIN_STEP_GATE[nextStep])
          : state.canGoToStep,
        loading: false,
        error: null,
      };
    }),
    on(resendOtpFailure, (state, { error }) => ({
      ...state,
      loading: false,
      error,
    })),
    on(reset, () => initialState),
    on(setLoading, (state, { loading }) => ({
      ...state,
      loading,
      error: loading ? null : state.error,
    })),
    on(setError, (state, { error }) => ({
      ...state,
      error,
      loading: false,
    })),
    on(clearError, state => ({
      ...state,
      error: null,
    })),
    on(backToCredentials, state => ({
      ...state,
      sessionToken: null,
      googleAuthRequired: false,
      googleAuthSetupRequired: false,
      googleAuthSetup: null,
      password: null,
      canGoToStep: 1,
      loading: false,
      error: null,
    }))
  ),
});
